/**
 * src/components/features/renfest/boothSettings.js
 * ═══════════════════════════════════════════════════════════════════════════
 * OPERATOR SETTINGS — the things a guest must never be asked.
 *
 * ⚠️ WHY THIS EXISTS: DESIGN-FUNDAMENTALS A7 — "if a choice needs explaining,
 * it is not a guest choice." Style, banner and prompt embellishment all fail
 * that test in a hot, noisy crowd. They are not deleted, they move here, where
 * someone who has read the manual sets them once for the whole event.
 *
 * ⚠️ PER DEVICE, ON PURPOSE. Same pattern as the inventory TV boards: a booth
 * is a physical machine sitting on a counter, and its operator configures THAT
 * machine. A server-side setting would be wrong the moment two booths run the
 * same event with different scenes.
 *
 * When this becomes the platform, this module is the seam: these values stop
 * coming from localStorage and start coming from the event's config row. The
 * shape should not have to change.
 */

const KEY = 'renfest.booth.settings.v1';

export const DEFAULTS = {
  likeness: 'light',       // ⚠️ a default must name a VISIBLE option (see STYLES in RenFestSelfie.jsx). 'full' was hidden 2026-08-31 and is visible again as of 2026-09-08, labelled "Cartoony", third in the list. 'light' stays the default on purpose: it is the safe one to land on when nobody reads the screen.
  banner: true,            // branding, not a preference
  extra: '',               // prompt embellishment; operator-only, never guest
  modelId: 'openai2-md',   // the painter. See RenFestSelfie for why there is no picker.
};

export function loadBoothSettings() {
  try {
    const raw = localStorage.getItem(KEY);
    // Spread over DEFAULTS so a settings blob written by an older build never
    // leaves a new key undefined.
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveBoothSettings(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...DEFAULTS, ...next }));
  } catch { /* private mode, quota, whatever — defaults still work */ }
}

/**
 * ⚠️ DO NOT ADD A CACHED SOUND CHECK HERE. I built one and it was wrong.
 *
 * This runs on the GUEST'S OWN PHONE, reached by a QR code. Every single guest
 * is a brand new device with an empty localStorage, so anything cached "per
 * device per day" is written once and read never — and worse, it quietly
 * assumes a booth-on-a-counter model that is not what this is. The volume
 * prompt belongs in the flow, shown to everyone, every time, because for every
 * guest it IS the first time.
 */

/**
 * ⚠️ VERSION THIS WHENEVER THE WORDING CHANGES.
 * Stored on every run. Consent copy gets reworded, and a run recorded against
 * v1 wording cannot be defended with v2 wording.
 */
/* ⚠️ DO NOT BUMP THIS FOR A PRIVACY-PAGE CHANGE. This string names the
   tap-to-agree wording ON THE BOOTH SCREEN, and it is paired by hand with a
   `consent_version` row in the database (sql/020, sql/022) — the server
   resolves `consent_version_id` from the LATEST row for the tenant, so a
   bumped string with no matching row writes rows whose id points at wording
   the guest never saw. That is the exact bug `022_consent_matches_the_booth`
   was written to fix.

   Bumped 2026-08-31 by mistake, for the /privacy update that describes the
   D-15 device code, and reverted the same day. The privacy policy and the
   booth's consent sentence are two different documents; only a change to the
   SENTENCE bumps this, and it needs a migration in the same commit. */
export const CONSENT_VERSION = 'v1-2026-08-06';

export const CONSENT_TEXT =
  "I'm 18+, or a parent or guardian is here with me - and I agree to my photo "
  + 'being used to make this portrait.';

export const CONSENT_SUBTEXT =
  'Your photos are sent to an AI service to paint your portrait. '
  + 'We never sell your images. See the notice at this booth.';
